import { type Static, Type } from "@sinclair/typebox"
import { CursorQuerySchema, createPageSchema, IsoDateTimeSchema, Nullable } from "./common"

// Clients M2M [V1] — docs/04 §2-§3 y docs/03 §3.

export const ApiClientStatusSchema = Type.Union([Type.Literal("active"), Type.Literal("disabled")])
export type ApiClientStatus = Static<typeof ApiClientStatusSchema>

export const ApiClientSchema = Type.Object({
  id: Type.String(),
  // null = client de instancia (acceso a todos los dominios); un id = acotado a ese tenant.
  domain_id: Nullable(Type.String()),
  name: Type.String(),
  client_id: Type.String(),
  scopes: Type.Array(Type.String()),
  status: ApiClientStatusSchema,
  last_used_at: Nullable(IsoDateTimeSchema),
  created_at: IsoDateTimeSchema,
  updated_at: IsoDateTimeSchema,
})
export type ApiClient = Static<typeof ApiClientSchema>

export const ListClientsQuerySchema = Type.Composite([
  CursorQuerySchema,
  Type.Object({
    domain: Type.Optional(Type.String()),
    q: Type.Optional(Type.String()),
    status: Type.Optional(ApiClientStatusSchema),
  }),
])
export type ListClientsQuery = Static<typeof ListClientsQuerySchema>

export const ApiClientPageSchema = createPageSchema(ApiClientSchema)
export type ApiClientPage = Static<typeof ApiClientPageSchema>

export const CreateClientRequestSchema = Type.Object({
  // Ausente o null ⇒ client de INSTANCIA (todos los dominios; solo lo crea el
  // admin de instancia). Un id ⇒ client acotado a ese dominio.
  domain_id: Type.Optional(Nullable(Type.String())),
  name: Type.String({ minLength: 1 }),
  scopes: Type.Array(Type.String()),
})
export type CreateClientRequest = Static<typeof CreateClientRequestSchema>

// El secret solo se devuelve UNA vez, al crear o rotar.
export const CreateClientResponseSchema = Type.Composite([
  ApiClientSchema,
  Type.Object({ client_secret: Type.String() }),
])
export type CreateClientResponse = Static<typeof CreateClientResponseSchema>

export const UpdateClientRequestSchema = Type.Object({
  name: Type.Optional(Type.String({ minLength: 1 })),
  scopes: Type.Optional(Type.Array(Type.String())),
  status: Type.Optional(ApiClientStatusSchema),
})
export type UpdateClientRequest = Static<typeof UpdateClientRequestSchema>

export const RotateSecretResponseSchema = Type.Object({
  client_id: Type.String(),
  client_secret: Type.String(),
})
export type RotateSecretResponse = Static<typeof RotateSecretResponseSchema>

// POST /v1/auth/token — client_credentials [V1]; scope delimitado por espacios (RFC 6749).
export const TokenRequestSchema = Type.Object({
  grant_type: Type.Literal("client_credentials"),
  client_id: Type.String(),
  client_secret: Type.String(),
  scope: Type.Optional(Type.String()),
})
export type TokenRequest = Static<typeof TokenRequestSchema>

// Sin refresh: los clients vuelven a solicitar el token (docs/06 §2).
export const TokenResponseSchema = Type.Object({
  token_type: Type.Literal("Bearer"),
  access_token: Type.String(),
  expires_in: Type.Integer(),
  scope: Type.Optional(Type.String()),
})
export type TokenResponse = Static<typeof TokenResponseSchema>

// POST /v1/token/introspect — RFC 7662 [V1].
export const IntrospectRequestSchema = Type.Object({
  token: Type.String(),
  token_type_hint: Type.Optional(
    Type.Union([Type.Literal("access_token"), Type.Literal("refresh_token")]),
  ),
})
export type IntrospectRequest = Static<typeof IntrospectRequestSchema>

// Campos opcionales presentes solo cuando active === true.
export const IntrospectResponseSchema = Type.Object({
  active: Type.Boolean(),
  scope: Type.Optional(Type.String()),
  client_id: Type.Optional(Type.String()),
  username: Type.Optional(Type.String()),
  token_type: Type.Optional(Type.String()),
  exp: Type.Optional(Type.Integer()),
  iat: Type.Optional(Type.Integer()),
  nbf: Type.Optional(Type.Integer()),
  sub: Type.Optional(Type.String()),
  aud: Type.Optional(Type.String()),
  iss: Type.Optional(Type.String()),
  jti: Type.Optional(Type.String()),
  // Claims propios de REMA Auth
  act: Type.Optional(Type.Union([Type.Literal("user"), Type.Literal("client")])),
  dom: Type.Optional(Type.String()),
})
export type IntrospectResponse = Static<typeof IntrospectResponseSchema>

// POST /v1/token/revoke — RFC 7009 [V1]; responde 200 sin body.
export const RevokeTokenRequestSchema = Type.Object({
  token: Type.String(),
  token_type_hint: Type.Optional(
    Type.Union([Type.Literal("access_token"), Type.Literal("refresh_token")]),
  ),
})
export type RevokeTokenRequest = Static<typeof RevokeTokenRequestSchema>
